import { CeremonyViewer } from '../ceremony-viewer/ceremony-viewer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { configureTenantAuth, hasActiveTenantSession } from '../../lib/tenant-auth';
import { createClioCeremony, enterClioCeremony, getClioShell, getClioPrefill, type ClioContactPrefill, type ClioEntry, type ClioShell } from '../../lib/clio-ceremonies';
import { CeremonyCreationForm } from '../components/ceremony-creation-form';
import { ClioSyncError } from '../components/clio-sync-error';

export function ClioCeremonyEntryPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [login, setLogin] = useState(false);
  // StrictMode re-runs effects; the same one-use nonce must have one request.
  const request = useRef<Promise<ClioEntry | null>>();
  useEffect(() => {
    let active = true;
    request.current ??= (async () => {
      await configureTenantAuth();
      if (!await hasActiveTenantSession()) return null;
      return enterClioCeremony(params);
    })();
    request.current.then((entry) => {
      if (!active) return;
      if (!entry) { setLogin(true); return; }
      navigate(`/integrations/clio/ceremonies/${encodeURIComponent(entry.tenantId)}/${entry.shell.remoteObject.id}`,
        { replace: true });
    }).catch((reason) => { if (active) setError(message(reason)); });
    return () => { active = false; };
  }, [params, navigate]);
  return <main className="mx-auto max-w-3xl px-6 py-12">
    <h1 className="mb-5 font-serif text-3xl">Witness Ceremony</h1>
    {error ? <p role="alert" className="text-red-800">{error}</p> : login ? <>
      <p className="mb-3">Sign in to Witness to open this Matter.</p>
      <Link className="underline" to={`/tenant/login?destination=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
        Sign in to Witness
      </Link>
    </> : <p>Opening Matter…</p>}
  </main>;
}

export function ClioCeremonyViewerPage() {
  const { tenantId = '', remoteObjectId = '' } = useParams();
  const [shell, setShell] = useState<ClioShell | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [contact, setContact] = useState<ClioContactPrefill | null>(null);
  const [prefillHint, setPrefillHint] = useState('Loading the Matter client from Clio…');
  useEffect(() => {
    let active = true;
    setContact(null);
    setPrefillHint('Loading the Matter client from Clio…');
    void configureTenantAuth().then(() => getClioPrefill(tenantId, remoteObjectId)).then(({ contact }) => {
      if (!active) return;
      setContact(contact);
      setPrefillHint(contact?.type === 'Person'
        ? `Prefilled from Clio: ${contact.name}. You can edit the subject for a representative or another person.`
        : contact ? `The Matter client is ${contact.name}, a company. Enter the person who will complete the ceremony.`
        : 'No visible client name was available from Clio. Enter the subject manually.');
    }).catch(() => { if (active) setPrefillHint('Clio contact prefill is unavailable. You can still enter the subject manually.'); });
    return () => { active = false; };
  }, [tenantId, remoteObjectId]);
  const reload = useCallback(async () => {
    await configureTenantAuth();
    const result = await getClioShell(tenantId, remoteObjectId);
    setShell(result.shell);
    setError(null);
  }, [tenantId, remoteObjectId]);
  useEffect(() => { reload().catch((reason) => setError(message(reason))); }, [reload]);
  return <main className="mx-auto max-w-3xl space-y-6 px-6 py-12">
    <header>
      <h1 className="font-serif text-3xl">Witness Ceremony</h1>
      {shell && <p className="mt-2 text-neutral-600">Clio Matter {contact?.matterNumber ?? shell.remoteObject.reference.remoteObjectId}</p>}
    </header>
    {error && <div role="alert" className="rounded border border-red-300 p-4 text-red-800">{error}
      <button className="ml-3 underline" onClick={() => reload().catch((reason) => setError(message(reason)))}>Reload</button>
    </div>}
    {notice && <p role="status" className="rounded border border-amber-300 bg-amber-50 p-4">{notice}</p>}
    {!shell && !error && <p>Loading ceremony…</p>}
    {shell && <>
      {shell.syncError && <ClioSyncError occurredAtMs={shell.syncError.occurredAtMs} />}
      {shell.latestCeremony ? <CeremonyViewer tenantId={tenantId} ceremonyId={shell.latestCeremony.id} />
        : <p>No ceremony has been created for this Matter.</p>}
      <button className="text-sm underline" onClick={() => reload().catch((reason) => setError(message(reason)))}>Refresh Matter status</button>
      <CeremonyCreationForm tenantId={tenantId} key={`${tenantId}/${remoteObjectId}`} initialSubjectName={contact?.type === 'Person' ? contact.name : ''} subjectHint={prefillHint} submitCeremony={async (input) => {
        await createClioCeremony(tenantId, remoteObjectId, input);
        setNotice('Ceremony created. Clio will update in the background.');
      }} onCreated={() => { reload().catch((reason) => setError(message(reason))); }} />
    </>}
  </main>;
}

function message(reason: unknown) { return reason instanceof Error ? reason.message : 'Unable to open this Matter.'; }
